"use client";

import {
  Button,
  cn,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { Building2, PlusIcon, ShoppingCart } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { PurchaseOrderListSortField } from "@/modules/merchant/domain/contracts";
import {
  canUseMerchantPermission,
  type PurchaseOrderListRow,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { PurchaseOrderStatusBadge } from "@/modules/merchant/presentation/ui/PurchaseOrderStatusBadge";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import {
  ShopPurchaseOrdersFilter,
  type ShopPurchaseOrdersListSearch,
} from "@/modules/merchant/presentation/ui/ShopPurchaseOrdersFilter";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { usePurchaseOrdersListQuery } from "../api/queries";

const LIST_ROUTE = "/app/shops/$shopId/purchase-orders" as const;
const NEW_ROUTE = "/app/shops/$shopId/purchase-orders/new" as const;
const DETAIL_ROUTE =
  "/app/shops/$shopId/purchase-orders/$purchaseOrderId" as const;
const SUPPLIERS_ROUTE = "/app/shops/$shopId/suppliers" as const;

/** ຫໍ່ເນື້ອໃນ cell ໃຫ້ກົດໄດ້ທັງ cell → ໄປໜ້າລາຍລະອຽດ PO (ຄືກັບກົດ ເລກ PO) */
function PoCellLink({
  shopId,
  purchaseOrderId,
  className,
  children,
}: {
  shopId: string | undefined;
  purchaseOrderId: string;
  className?: string;
  children: ReactNode;
}) {
  if (!shopId) return <>{children}</>;
  return (
    <Link
      to={DETAIL_ROUTE}
      params={{ shopId, purchaseOrderId }}
      className={cn("block text-center", className)}
    >
      {children}
    </Link>
  );
}

export function ShopPurchaseOrdersPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: LIST_ROUTE });
  const search = (useSearch({
    from: LIST_ROUTE,
  }) ?? {}) as ShopPurchaseOrdersListSearch;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;

  const status =
    search.status != null && search.status.trim() !== ""
      ? (search.status as
          | "draft"
          | "submitted"
          | "partially_received"
          | "received"
          | "canceled")
      : undefined;
  const branchId =
    search.branchId != null && search.branchId.trim() !== ""
      ? search.branchId.trim()
      : undefined;
  const supplierId =
    search.supplierId != null && search.supplierId.trim() !== ""
      ? search.supplierId.trim()
      : undefined;
  const due =
    search.due === "soon" || search.due === "overdue" ? search.due : undefined;

  const sortBy = search.sortBy;
  const sortOrder = search.sortOrder;

  const query = useMemo(
    () => ({
      limit,
      offset,
      status,
      branchId,
      supplierId,
      due,
      sortBy,
      sortOrder,
    }),
    [limit, offset, status, branchId, supplierId, due, sortBy, sortOrder],
  );

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canAdjust = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );

  const list = usePurchaseOrdersListQuery(shopId, query);
  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;

  const columns: TanstackReactTable.ColumnDef<PurchaseOrderListRow>[] =
    useMemo(() => {
      const poBase = createSortableColumn<PurchaseOrderListRow>(
        "poNumber",
        "ເລກ PO",
        { size: 140 },
      );
      const statusBase = createSortableColumn<PurchaseOrderListRow>(
        "status",
        "ສະຖານະ",
        { size: 120 },
      );
      const branchBase = createSortableColumn<PurchaseOrderListRow>(
        "branchName",
        "ສາຂາ",
        { size: 160 },
      );
      const supplierCol: TanstackReactTable.ColumnDef<PurchaseOrderListRow> = {
        id: "supplier",
        header: "ຜູ້ສະໜອງ",
        size: 180,
        cell: ({ row }) => {
          const n = row.original.supplierDisplayName?.trim();
          return (
            <span className="text-sm">
              {n ? n : <span className="text-muted-foreground">—</span>}
            </span>
          );
        },
      };
      const lineCountCol = createSortableColumn<PurchaseOrderListRow>(
        "lineCount",
        "ຈຳນວນລາຍການ",
        { size: 120 },
      );
      const totalCol: TanstackReactTable.ColumnDef<PurchaseOrderListRow> = {
        id: "totalAmount",
        header: "ຍອດລວມ",
        size: 150,
        cell: ({ row }) => (
          <PoCellLink shopId={shopId} purchaseOrderId={row.original.id}>
            <span className="font-medium text-sm tabular-nums">
              {row.original.totalAmount.toLocaleString("en-US")}{" "}
              <span className="text-muted-foreground text-xs">
                {row.original.currency}
              </span>
            </span>
          </PoCellLink>
        ),
      };
      const createdBase = createSortableColumn<PurchaseOrderListRow>(
        "createdAt",
        "ສ້າງເມື່ອ",
        { size: 160 },
      );

      return [
        {
          ...poBase,
          cell: ({ row }) => (
            <PoCellLink
              shopId={shopId}
              purchaseOrderId={row.original.id}
              className="font-medium text-primary hover:underline"
            >
              {row.original.poNumber}
            </PoCellLink>
          ),
        },
        {
          ...statusBase,
          cell: ({ row }) => (
            <PoCellLink shopId={shopId} purchaseOrderId={row.original.id}>
              <PurchaseOrderStatusBadge status={row.original.status} />
            </PoCellLink>
          ),
        },
        {
          ...branchBase,
          cell: ({ row }) => {
            const n = row.original.branchName;
            return (
              <PoCellLink shopId={shopId} purchaseOrderId={row.original.id}>
                {n?.trim() ? (
                  <span className="text-sm">{n}</span>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {row.original.branchId.slice(0, 8)}…
                  </span>
                )}
              </PoCellLink>
            );
          },
        },
        {
          ...supplierCol,
          cell: ({ row }) => {
            const n = row.original.supplierDisplayName?.trim();
            return (
              <PoCellLink shopId={shopId} purchaseOrderId={row.original.id}>
                <span className="text-sm">
                  {n ? n : <span className="text-muted-foreground">—</span>}
                </span>
              </PoCellLink>
            );
          },
        },
        {
          ...lineCountCol,
          cell: ({ row }) => (
            <PoCellLink shopId={shopId} purchaseOrderId={row.original.id}>
              <span className="text-sm tabular-nums">
                {row.original.lineCount}
              </span>
            </PoCellLink>
          ),
        },
        totalCol,
        {
          ...createdBase,
          cell: ({ row }) => (
            <PoCellLink shopId={shopId} purchaseOrderId={row.original.id}>
              <span className="text-muted-foreground text-xs">
                {formatDateTimeLocal(row.original.createdAt)}
              </span>
            </PoCellLink>
          ),
        },
      ];
    }, [shopId]);

  if (!shopId) return null;

  const tableSortBy: PurchaseOrderListSortField = search.sortBy ?? "createdAt";
  const tableSortOrder: "asc" | "desc" = search.sortOrder ?? "desc";

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.purchaseOrders(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 font-heading font-semibold text-xl tracking-tight">
              <ShoppingCart className="size-6 text-primary" aria-hidden />
              ໃບສັ່ງຊື້ (PO)
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ວາງແຜນສັ່ງຈາກຜູ້ສະໜອງ — ກອງຕາມສາຂາ ແລະສະຖານະ; ສະກຸນເງິນຕໍ່ໃບຕ້ອງກົງກັບ SKU
              ແຕ່ລະບັນທັດ
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => nav({ to: SUPPLIERS_ROUTE, params: { shopId } })}
            >
              <Building2 className="size-4" aria-hidden />
              ຜູ້ສະໜອງ
            </Button>
            {canAdjust ? (
              <Button
                type="button"
                variant="default"
                className="gap-1"
                onClick={() => {
                  nav({ to: NEW_ROUTE, params: { shopId } });
                }}
              >
                <PlusIcon className="size-4" aria-hidden />
                ສ້າງ PO
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ShopPurchaseOrdersFilter />
          {/* ຈັດຫົວຄໍລໍາທຸກອັນໄວ້ກາງ ໃຫ້ຕົງກັບຄ່າ (cell) ທີ່ center */}
          <div className="[&_thead_th>div]:justify-center">
            <DataTable<PurchaseOrderListRow, unknown>
              noDataMessage="ບໍ່ມີລາຍການທີ່ກົງກັບຕົວກອງ."
              isLoading={list.isLoading}
              columns={columns}
              data={rows}
              offset={offset}
              limit={limit}
              totalCount={total}
              sortBy={tableSortBy}
              sortOrder={tableSortOrder}
              onSortingChange={(sorting) => {
                if (!shopId) return;
                const s = sorting?.[0];
                if (!s?.id) return;
                nav({
                  to: LIST_ROUTE,
                  params: { shopId },
                  search: {
                    ...search,
                    offset: 0,
                    sortBy: s.id as PurchaseOrderListSortField,
                    sortOrder: s.desc ? "desc" : "asc",
                  },
                });
              }}
              onPaginationChange={(p) => {
                if (!shopId) return;
                nav({
                  to: LIST_ROUTE,
                  params: { shopId },
                  search: {
                    ...search,
                    offset: p.offset,
                    limit: p.limit,
                  },
                });
              }}
            />
          </div>
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
